import { Icon } from "@evidenceb/athena-common/design-system/Icon";
import { useFeatureFlags } from "@evidenceb/athena-common/modules/FeatureFlags";
import type { AthenaEvent } from "@evidenceb/athena-event-storage-schemas";
import type { Subscriber } from "@evidenceb/athena-events-tools";
import * as Sentry from "@sentry/react";
import React, { useEffect } from "react";
import { type IntlShape, useIntl } from "react-intl";
import { toast } from "react-toastify";
import {
  type AthenaEventStreamPublisher,
  useAthenaEventStreamPublisher,
  useInitializingEventStream,
} from "../../contexts/EventStreamContext";
import { buildMessage, catalog } from "../../utils/motivMessages";

const isImmediateDuplicate = (
  signatureFunction: (athenaEvent: any) => string,
) => {
  let previousSignature = "";
  return (athenaEvent: any) => {
    const signature = signatureFunction(athenaEvent);
    if (signature === previousSignature) {
      return true;
    }
    previousSignature = signature;
    return false;
  };
};

const getMotivatorSubscriber = (
  intl: IntlShape,
  athenaEventStreamPublisher: AthenaEventStreamPublisher,
) => {
  const duplicateChecker = isImmediateDuplicate((athenaEvent) => {
    return athenaEvent.data.exerciseId + athenaEvent.data.messageType;
  });
  return {
    id: "toaster",
    athenaEventTypeFilter: "AIShowMessageEvent",
    async processAthenaEvent(
      athenaEvent: AthenaEvent<"ai", "AIShowMessageEvent">,
    ) {
      // Do not display motivational messages for events coming from a
      // pull.
      if (athenaEvent.insertedAt) {
        return;
      }
      // check that the event type is in catalog
      const type = athenaEvent.data.messageType;
      if (type in catalog) {
        if (duplicateChecker(athenaEvent)) return;
        let message: React.ReactNode;
        try {
          message = buildMessage(athenaEvent, intl, athenaEventStreamPublisher);
        } catch (e) {
          console.error(
            `unsupported athena-event: ${JSON.stringify(athenaEvent)}`,
            e,
          );
          return;
        }
        const ico = catalog[type].ico;
        toast(message, {
          toastId: athenaEvent.id,
          containerId: "progress-notifier",
          autoClose: type === "blockedMessage" ? false : 8000,
          closeOnClick: false,
          icon: ico
            ? React.createElement(Icon, {
                path: `motivationIcons/${ico}`,
                size: "medium",
              })
            : null,
          progressClassName: ico
            ? `progress-${ico.replaceAll("_", "-")}`
            : undefined,
        });
      }
    },
  };
};

export const useEventStreamEarlySubscribers = () => {
  const flags = useFeatureFlags();
  const intl = useIntl();
  const athenaEventStream = useInitializingEventStream();
  const athenaEventStreamPublisher = useAthenaEventStreamPublisher();
  useEffect(() => {
    if (!athenaEventStream) {
      return;
    }
    if (flags.motivationalMessages) {
      const motivator = getMotivatorSubscriber(
        intl,
        athenaEventStreamPublisher,
      );
      athenaEventStream.registerSubscriber(motivator);
      return () => {
        athenaEventStream.unregisterSubscriber(motivator.id);
      };
    }
  }, [athenaEventStream, intl, flags, athenaEventStreamPublisher]);
  useEffect(() => {
    if (!athenaEventStream) {
      return;
    }
    const sentryBreadcrumbSubscriber: Subscriber = {
      id: "sentryBreadcrumbSubscriber",
      async processAthenaEvent(athenaEvent: any) {
        Sentry.addBreadcrumb({
          category: "athenaEvent",
          message: `Received AthenaEvent: ${JSON.stringify(athenaEvent)}`,
          level: "info",
        });
      },
    };
    athenaEventStream.registerSubscriber(sentryBreadcrumbSubscriber);
    return () => {
      athenaEventStream.unregisterSubscriber(sentryBreadcrumbSubscriber.id);
    };
  }, [athenaEventStream]);
};
